import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "sonner";

import {
  PersonaHookVariationsStatusResultSchema,
  REFERENCE_VIDEO_TOO_LONG,
} from "@nexus/contracts";

import {
  confirmPersonaHookVariations,
  deletePersonaScripts,
  fetchPersonaOpportunityScripts,
  fetchPersonaOpportunitySetScripts,
  fetchPersonaScriptVersionHistory,
  generatePersonaOpportunityScripts,
  generatePersonaStoryboard,
  patchPersonaScriptContent,
  restorePersonaScriptVersion,
  togglePersonaScriptFavorite,
  aiEditPersonaScripts,
  suggestPersonaHookVariations,
  suggestPersonaScripts,
  importPersonaScriptFromText,
  importPersonaScriptsFromLegacy,
  extractPersonaScriptImport,
  fetchLegacyOpportunitySets,
  fetchLegacyOpportunitySetScripts,
  resolvePersonaReferenceLinks,
  type PersonaScriptContentPatch,
  type PersonaScriptDto,
  type PersonaScriptOutputType,
  type PersonaScriptVideoReference,
} from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import i18n from "@/lib/i18n";
import { ApiError, getLocalUserFacingApiMessage } from "@/lib/api-client";
import { Sentry } from "@/lib/sentry";
import { useAmberMutation } from "@/lib/use-amber-mutation";
import type { StatusResult } from "@/features/identity-hub/api/identity-hub.api";

import { revealGenerationTask } from "./opportunity-sets.queries";
import {
  normalizedPersonaOpportunitySetTenantId,
  personaOpportunitySetKeys,
} from "./persona-opportunity-sets.queries";

/** LLM script runs can take several minutes — generous Amber poll budget. */
export const PERSONA_SCRIPTS_POLL_MAX_ATTEMPTS = 120;
const PERSONA_SCRIPTS_POLL_INTERVAL_MS = 1_000;

function readScriptsPersisted(result: StatusResult): number {
  return typeof result.scriptsPersisted === "number" ? result.scriptsPersisted : 0;
}

function readBriefsPersisted(result: StatusResult): number {
  return typeof result.briefsPersisted === "number" ? result.briefsPersisted : 0;
}

function readOpportunityResultRows(result: StatusResult): Array<Record<string, unknown>> {
  const rows = result.opportunityResults;
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (entry): entry is Record<string, unknown> =>
      !!entry && typeof entry === "object",
  );
}

/** Attempted scripts = created + updated + failed across opportunity results. */
function readScriptsAttempted(result: StatusResult): number {
  return readOpportunityResultRows(result).reduce((sum, row) => {
    const created = typeof row.scriptsCreated === "number" ? row.scriptsCreated : 0;
    const updated = typeof row.scriptsUpdated === "number" ? row.scriptsUpdated : 0;
    const failed = typeof row.scriptsFailed === "number" ? row.scriptsFailed : 0;
    return sum + created + updated + failed;
  }, 0);
}

function statusHasScriptFailures(result: StatusResult): boolean {
  if (result.hasErrors === true) return true;
  return readOpportunityResultRows(result).some((row) => {
    if (row.error) return true;
    return typeof row.scriptsFailed === "number" && row.scriptsFailed > 0;
  });
}

function readBriefsAttempted(result: StatusResult): number {
  return readOpportunityResultRows(result).reduce((sum, row) => {
    const created = typeof row.briefsCreated === "number" ? row.briefsCreated : 0;
    const failed = typeof row.briefsFailed === "number" ? row.briefsFailed : 0;
    return sum + created + failed;
  }, 0);
}

function statusHasBriefFailures(result: StatusResult): boolean {
  return readOpportunityResultRows(result).some(
    (row) => typeof row.briefsFailed === "number" && row.briefsFailed > 0,
  );
}

/** Immediate cancel feedback — do not wait for Amber COMPLETED. */
export function notifyPersonaScriptsGenerationCancelled(): void {
  toast.info(
    i18n.t("personaOpportunities.bulk.generationCancelledNone", {
      defaultValue: "Generation cancelled",
    }),
  );
}

function toastPersonaGeneratePartial(result: StatusResult, scriptsPersisted: number, briefsPersisted: number): void {
  const includeBriefs = briefsPersisted > 0 || statusHasBriefFailures(result);
  const includeScripts =
    scriptsPersisted > 0 || readScriptsAttempted(result) > 0 || !includeBriefs;

  if (includeScripts && includeBriefs) {
    toast.warning(
      i18n.t("personaOpportunities.bulk.generatePartialBoth", {
        defaultValue:
          "Generated {{scriptsPersisted}} scripts and {{briefsPersisted}} briefs — check the Scripts tab for results",
        scriptsPersisted,
        briefsPersisted,
      }),
    );
    return;
  }
  if (includeBriefs) {
    const briefsAttempted = readBriefsAttempted(result);
    if (briefsAttempted > 0) {
      toast.warning(
        i18n.t("personaOpportunities.bulk.generatePartialBriefs", {
          defaultValue:
            "Generated {{persisted}} of {{total}} briefs — check the Scripts tab for results",
          persisted: briefsPersisted,
          total: Math.max(briefsAttempted, briefsPersisted),
        }),
      );
      return;
    }
    toast.warning(
      i18n.t("personaOpportunities.bulk.generatePartialBriefsSaved", {
        defaultValue: "Generated {{count}} briefs — check the Scripts tab for results",
        count: briefsPersisted,
      }),
    );
    return;
  }
  const total = Math.max(readScriptsAttempted(result), scriptsPersisted);
  toast.warning(
    i18n.t("personaOpportunities.bulk.generatePartial", {
      defaultValue:
        "Generated {{persisted}} of {{total}} scripts — check the Scripts tab for results",
      persisted: scriptsPersisted,
      total,
    }),
  );
}

function toastPersonaGenerateSuccess(scriptsPersisted: number, briefsPersisted: number): void {
  if (scriptsPersisted > 0 && briefsPersisted > 0) {
    toast.success(
      i18n.t("personaOpportunities.bulk.generateSuccessBoth", {
        defaultValue: "Generated {{scriptCount}} scripts and {{briefCount}} briefs",
        scriptCount: scriptsPersisted,
        briefCount: briefsPersisted,
      }),
    );
    return;
  }
  if (briefsPersisted > 0) {
    toast.success(
      i18n.t("personaOpportunities.bulk.generateSuccessBriefs", {
        defaultValue_one: "Generated {{count}} brief",
        defaultValue_other: "Generated {{count}} briefs",
        count: briefsPersisted,
      }),
    );
    return;
  }
  toast.success(
    i18n.t("personaOpportunities.bulk.generateSuccess", {
      defaultValue_one: "Generated {{count}} script",
      defaultValue_other: "Generated {{count}} scripts",
      count: scriptsPersisted,
    }),
  );
}

function toastPersonaGenerateOutcome(result: StatusResult): void {
  if (result.cancelled === true) {
    return;
  }
  const scriptsPersisted = readScriptsPersisted(result);
  const briefsPersisted = readBriefsPersisted(result);
  if (statusHasScriptFailures(result) || statusHasBriefFailures(result)) {
    toastPersonaGeneratePartial(result, scriptsPersisted, briefsPersisted);
    return;
  }
  toastPersonaGenerateSuccess(scriptsPersisted, briefsPersisted);
}

// ─── Key Factory ────────────────────────────────────────────────────

export const personaOpportunityScriptKeys = {
  all: [...personaOpportunitySetKeys.all, "scripts"] as const,
  bySet: (tenantId: string, setId: string) =>
    [...personaOpportunityScriptKeys.all, "set", tenantId, setId] as const,
  byOpportunity: (tenantId: string, setId: string, opportunityId: string) =>
    [
      ...personaOpportunityScriptKeys.bySet(tenantId, setId),
      "opportunity",
      opportunityId,
    ] as const,
  versions: (tenantId: string, setId: string, scriptId: string) =>
    [...personaOpportunityScriptKeys.bySet(tenantId, setId), "versions", scriptId] as const,
  version: (tenantId: string, setId: string, scriptId: string, version: number) =>
    [...personaOpportunityScriptKeys.versions(tenantId, setId, scriptId), version] as const,
  legacySets: (tenantId: string) =>
    [...personaOpportunityScriptKeys.all, "legacy-sets", tenantId] as const,
  legacyScripts: (tenantId: string, legacySetId: string, search: string) =>
    [...personaOpportunityScriptKeys.all, "legacy-scripts", tenantId, legacySetId, search] as const,
} as const;

const LEGACY_SCRIPTS_PAGE_SIZE = 20;

export function legacyOpportunitySetsQueryOptions(
  tenantId: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityScriptKeys.legacySets(normalizedTenant),
    queryFn: fetchLegacyOpportunitySets,
    enabled: (options.enabled ?? true) && Boolean(tenantId),
  });
}

export function legacyOpportunitySetScriptsInfiniteQueryOptions(
  tenantId: string | null | undefined,
  legacySetId: string | null | undefined,
  search: string,
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  const trimmedSearch = search.trim();
  return infiniteQueryOptions({
    queryKey: personaOpportunityScriptKeys.legacyScripts(
      normalizedTenant,
      legacySetId ?? "",
      trimmedSearch,
    ),
    queryFn: ({ pageParam }) =>
      fetchLegacyOpportunitySetScripts(legacySetId ?? "", {
        cursor: pageParam,
        limit: LEGACY_SCRIPTS_PAGE_SIZE,
        search: trimmedSearch || undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: (options.enabled ?? true) && Boolean(tenantId) && Boolean(legacySetId),
  });
}

// ─── Query Options ──────────────────────────────────────────────────

export function personaOpportunityScriptsQueryOptions(
  tenantId: string | null | undefined,
  setId: string,
  opportunityId: string,
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  const enabled = options.enabled ?? true;
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityScriptKeys.byOpportunity(
      normalizedTenant,
      setId,
      opportunityId,
    ),
    queryFn: (): Promise<PersonaScriptDto[]> =>
      fetchPersonaOpportunityScripts(setId, opportunityId),
    enabled: !!tenantId && !!setId && !!opportunityId && enabled,
    refetchInterval: options.refetchInterval ?? false,
  });
}

/** Set-level scripts list — preferred for Scripts tab (one request, one poll). */
export function personaOpportunitySetScriptsQueryOptions(
  tenantId: string | null | undefined,
  setId: string,
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  const enabled = options.enabled ?? true;
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityScriptKeys.bySet(normalizedTenant, setId),
    queryFn: (): Promise<PersonaScriptDto[]> => fetchPersonaOpportunitySetScripts(setId),
    enabled: !!tenantId && !!setId && enabled,
    refetchInterval: options.refetchInterval ?? false,
  });
}

/** While generate is running, list scripts at the same 2s cadence as set generation. */
export const PERSONA_SCRIPTS_LIVE_REFETCH_MS = 2_000;

// ─── Mutations ──────────────────────────────────────────────────────

export interface GeneratePersonaOpportunityScriptsVariables {
  setId: string;
  tenantId: string;
  personaOpportunityIds: string[];
  outputType?: PersonaScriptOutputType;
  scriptsPerAngle?: number;
  videoReference?: PersonaScriptVideoReference;
  scriptGuidelines?: string;
  guidelinesUploadId?: string;
}

export interface UseGeneratePersonaOpportunityScriptsOptions {
  /** Fires after generate POST 202 — close the modal before Amber polling. */
  onAccepted?: (variables: GeneratePersonaOpportunityScriptsVariables) => void;
}

function isReferenceVideoTooLong(error: unknown): boolean {
  return error instanceof ApiError && error.code === REFERENCE_VIDEO_TOO_LONG;
}

/**
 * Amber Path: POST generate → poll `/persona-opportunity-sets/status/:id`.
 * Opens the generation task sidebar on the 202; invalidates scripts + set caches on success.
 * Zero persisted → backend FAILED (error toast). Partial persist → warning toast.
 */
export function useGeneratePersonaOpportunityScripts(
  options: UseGeneratePersonaOpportunityScriptsOptions = {},
) {
  const queryClient = useQueryClient();

  return useAmberMutation<GeneratePersonaOpportunityScriptsVariables>({
    mutationFn: async ({
      setId,
      tenantId,
      personaOpportunityIds,
      outputType,
      scriptsPerAngle,
      videoReference,
      scriptGuidelines,
      guidelinesUploadId,
    }) => {
      const trimmedGuidelines = scriptGuidelines?.trim();
      const result = await generatePersonaOpportunityScripts(setId, {
        tenantId,
        idempotencyKey: crypto.randomUUID(),
        personaOpportunityIds,
        ...(outputType ? { outputType } : {}),
        ...(scriptsPerAngle != null ? { scriptsPerAngle } : {}),
        ...(videoReference ? { videoReference } : {}),
        ...(trimmedGuidelines ? { scriptGuidelines: trimmedGuidelines } : {}),
        ...(trimmedGuidelines && guidelinesUploadId ? { guidelinesUploadId } : {}),
      });
      revealGenerationTask(queryClient);
      return result;
    },
    onAccepted: options.onAccepted,
    skipErrorToast: isReferenceVideoTooLong,
    pollBasePath: "/persona-opportunity-sets",
    invalidateKeys: [personaOpportunityScriptKeys.all, personaOpportunitySetKeys.all],
    maxAttempts: PERSONA_SCRIPTS_POLL_MAX_ATTEMPTS,
    intervalMs: PERSONA_SCRIPTS_POLL_INTERVAL_MS,
    onSuccess: (result) => {
      toastPersonaGenerateOutcome(result);
    },
  });
}

export function parsePersonaHookVariationsStatus(result: StatusResult) {
  const parsed = PersonaHookVariationsStatusResultSchema.safeParse(result);
  return parsed.success ? parsed.data : null;
}

export interface SuggestPersonaHookVariationsVariables {
  setId: string;
  scriptId: string;
  tenantId: string;
}

export interface UseSuggestPersonaHookVariationsOptions {
  onCompleted?: (args: {
    correlationId: string;
    parentScriptId: string;
    variants: NonNullable<ReturnType<typeof parsePersonaHookVariationsStatus>>["variants"];
  }) => void;
}

/**
 * Amber Path: POST suggest → poll `/persona-opportunity-sets/status/:id`.
 * Does not invalidate script lists — suggestions stay ephemeral until confirm.
 */
export function useSuggestPersonaHookVariations(
  options: UseSuggestPersonaHookVariationsOptions = {},
) {
  const queryClient = useQueryClient();
  const lastCorrelationIdByScript = useRef(new Map<string, string>());

  return useAmberMutation<SuggestPersonaHookVariationsVariables>({
    mutationFn: async ({ setId, scriptId, tenantId }) => {
      const result = await suggestPersonaHookVariations(setId, scriptId, {
        tenantId,
        idempotencyKey: crypto.randomUUID(),
      });
      lastCorrelationIdByScript.current.set(scriptId, result.correlationId);
      revealGenerationTask(queryClient);
      return result;
    },
    pollBasePath: "/persona-opportunity-sets",
    maxAttempts: PERSONA_SCRIPTS_POLL_MAX_ATTEMPTS,
    intervalMs: PERSONA_SCRIPTS_POLL_INTERVAL_MS,
    onSuccess: (result, variables) => {
      if (result.cancelled === true) {
        toast.info(
          i18n.t("personaOpportunities.scripts.hookVariations.cancelled", {
            defaultValue: "Hook generation cancelled",
          }),
        );
        return;
      }
      const parsed = parsePersonaHookVariationsStatus(result);
      const correlationId = lastCorrelationIdByScript.current.get(variables.scriptId);
      if (!parsed || !correlationId || parsed.parentScriptId !== variables.scriptId) {
        return;
      }
      options.onCompleted?.({
        correlationId,
        parentScriptId: parsed.parentScriptId,
        variants: parsed.variants,
      });
    },
  });
}

export interface ConfirmPersonaHookVariationsVariables {
  setId: string;
  scriptId: string;
  tenantId: string;
  correlationId: string;
  parentScriptId: string;
  keptIndexes: number[];
}

/** Sync persist of kept hook variants — invalidates script list + set detail/count keys. */
export function useConfirmPersonaHookVariations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      setId,
      scriptId,
      tenantId,
      correlationId,
      parentScriptId,
      keptIndexes,
    }: ConfirmPersonaHookVariationsVariables) =>
      confirmPersonaHookVariations(setId, scriptId, {
        tenantId,
        idempotencyKey: crypto.randomUUID(),
        correlationId,
        parentScriptId,
        keptIndexes,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: personaOpportunityScriptKeys.all }),
        queryClient.invalidateQueries({ queryKey: personaOpportunitySetKeys.all }),
      ]);
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

const PERSONA_AI_EDIT_POLL_MAX_ATTEMPTS = 60;
const PERSONA_AI_EDIT_POLL_INTERVAL_MS = 1_000;

export interface AiEditPersonaScriptsVariables {
  setId: string;
  scriptIds: string[];
  instruction: string;
  tenantId: string;
  cell?: { sceneNumber: number; field: "seeing" | "hearing" | "product" | "props" };
}

function countPersonaAiEditResults(result: StatusResult): {
  successCount: number;
  errorCount: number;
} {
  const rows = result.results;
  if (!Array.isArray(rows)) return { successCount: 0, errorCount: 0 };
  let successCount = 0;
  let errorCount = 0;
  for (const entry of rows) {
    if (!entry || typeof entry !== "object") continue;
    const status = (entry as { status?: unknown }).status;
    if (status === "success") successCount += 1;
    if (status === "error") errorCount += 1;
  }
  return { successCount, errorCount };
}

export function useAiEditPersonaScripts() {
  const queryClient = useQueryClient();

  return useAmberMutation<AiEditPersonaScriptsVariables>({
    mutationFn: async ({ setId, scriptIds, instruction, tenantId, cell }) => {
      const result = await aiEditPersonaScripts({ setId, scriptIds, instruction, tenantId, cell });
      revealGenerationTask(queryClient);
      return result;
    },
    pollBasePath: "/persona-opportunity-sets",
    invalidateKeys: [],
    onSuccess: (result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: personaOpportunityScriptKeys.all,
      });
      if (result.cancelled === true) {
        const { successCount } = countPersonaAiEditResults(result);
        toast.info(
          i18n.t("personaOpportunities.scripts.aiEditor.cancelled", {
            defaultValue: "Edit cancelled — {{count}} script(s) saved",
            count: successCount,
          }),
        );
        return;
      }
      const { successCount, errorCount } = countPersonaAiEditResults(result);
      if (errorCount > 0 && successCount === 0) {
        toast.error(
          i18n.t("personaOpportunities.scripts.aiEditor.allFailed", {
            defaultValue: "Could not update the selected scripts",
          }),
        );
        return;
      }
      if (errorCount > 0) {
        toast.warning(
          i18n.t("personaOpportunities.scripts.aiEditor.partialSuccess", {
            defaultValue: "Updated {{successCount}} scripts; {{errorCount}} failed",
            successCount,
            errorCount,
          }),
        );
        return;
      }
      if (variables.cell) {
        toast.success(
          i18n.t("personaOpportunities.scripts.cell.aiUpdated", {
            defaultValue: "Cell updated",
          }),
        );
        return;
      }
      toast.success(
        i18n.t("personaOpportunities.scripts.aiEditor.success", {
          defaultValue: "Script updated successfully",
        }),
      );
    },
    maxAttempts: PERSONA_AI_EDIT_POLL_MAX_ATTEMPTS,
    intervalMs: PERSONA_AI_EDIT_POLL_INTERVAL_MS,
  });
}

const PERSONA_STORYBOARD_POLL_MAX_ATTEMPTS = 60;
const PERSONA_STORYBOARD_POLL_INTERVAL_MS = 1_000;

export interface GeneratePersonaStoryboardVariables {
  scriptId: string;
  tenantId: string;
  variant?: "sketch" | "panels";
}

export function useGeneratePersonaStoryboard(setId: string) {
  const queryClient = useQueryClient();

  return useAmberMutation<GeneratePersonaStoryboardVariables>({
    mutationFn: async ({ scriptId, tenantId, variant }) => {
      const result = await generatePersonaStoryboard(setId, scriptId, tenantId, variant);
      revealGenerationTask(queryClient);
      return result;
    },
    pollBasePath: "/persona-opportunity-sets",
    invalidateKeys: [],
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: personaOpportunityScriptKeys.all,
      });
      if (result.cancelled === true) {
        toast.info(
          i18n.t("personaOpportunities.scripts.actions.storyboardCancelled", {
            defaultValue: "Storyboard generation cancelled",
          }),
        );
        return;
      }
      toast.success(
        i18n.t("personaOpportunities.scripts.actions.storyboardSuccess", {
          defaultValue: "Storyboard generated successfully",
        }),
      );
    },
    maxAttempts: PERSONA_STORYBOARD_POLL_MAX_ATTEMPTS,
    intervalMs: PERSONA_STORYBOARD_POLL_INTERVAL_MS,
  });
}

export interface PatchPersonaScriptContentVariables {
  setId: string;
  scriptId: string;
  tenantId: string;
  patch: PersonaScriptContentPatch;
}

/** Sync persist of a manual title or scene-cell edit. */
export function usePatchPersonaScriptContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, scriptId, tenantId, patch }: PatchPersonaScriptContentVariables) =>
      patchPersonaScriptContent(setId, scriptId, tenantId, patch),
    onSuccess: async (updated, variables) => {
      const normalizedTenant = normalizedPersonaOpportunitySetTenantId(variables.tenantId);
      queryClient.setQueryData<PersonaScriptDto[]>(
        personaOpportunityScriptKeys.bySet(normalizedTenant, variables.setId),
        (current) =>
          current?.map((script) => (script.id === updated.id ? updated : script)) ?? current,
      );
      await queryClient.invalidateQueries({ queryKey: personaOpportunityScriptKeys.all });
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export interface TogglePersonaScriptFavoriteVariables {
  setId: string;
  scriptId: string;
  tenantId: string;
  isFavorite: boolean;
}

interface TogglePersonaScriptFavoriteContext {
  key: readonly unknown[];
  previous: PersonaScriptDto[] | undefined;
}

/** Optimistic persist of the script star. */
export function useTogglePersonaScriptFavorite() {
  const queryClient = useQueryClient();

  return useMutation<
    { id: string; isFavorite: boolean },
    Error,
    TogglePersonaScriptFavoriteVariables,
    TogglePersonaScriptFavoriteContext
  >({
    mutationFn: ({ setId, scriptId, tenantId, isFavorite }) =>
      togglePersonaScriptFavorite(setId, scriptId, tenantId, isFavorite),
    onMutate: async ({ setId, tenantId, scriptId, isFavorite }) => {
      const key = personaOpportunityScriptKeys.bySet(
        normalizedPersonaOpportunitySetTenantId(tenantId),
        setId,
      );
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PersonaScriptDto[]>(key);
      queryClient.setQueryData<PersonaScriptDto[]>(key, (current) =>
        current?.map((script) => (script.id === scriptId ? { ...script, isFavorite } : script)) ??
        current,
      );
      return { key, previous };
    },
    onError: (err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    onSettled: (_data, _err, variables) => {
      void queryClient.invalidateQueries({
        queryKey: personaOpportunityScriptKeys.bySet(
          normalizedPersonaOpportunitySetTenantId(variables.tenantId),
          variables.setId,
        ),
      });
    },
    meta: { skipGlobalError: true },
  });
}

export interface DeletePersonaScriptsVariables {
  setId: string;
  tenantId: string;
  scriptIds: string[];
}

/** Soft-delete selected scripts; invalidates list + set counts. */
export function useDeletePersonaScripts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, tenantId, scriptIds }: DeletePersonaScriptsVariables) =>
      deletePersonaScripts(setId, {
        tenantId,
        idempotencyKey: crypto.randomUUID(),
        scriptIds,
      }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: personaOpportunityScriptKeys.all }),
        queryClient.invalidateQueries({ queryKey: personaOpportunitySetKeys.all }),
      ]);
      if (result.deletedIds.length === 0) return;
      toast.success(
        i18n.t("personaOpportunities.scripts.bulk.deleteToast", {
          count: result.deletedIds.length,
          defaultValue_one: "{{count}} script deleted",
          defaultValue_other: "{{count}} scripts deleted",
        }),
      );
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

const RESOLVE_LINKS_POLL_MAX_ATTEMPTS = 30;
const RESOLVE_LINKS_POLL_INTERVAL_MS = 500;

export interface SuggestPersonaScriptsVariables {
  setId: string;
  tenantId: string;
  description: string;
  variations: number;
  referenceUrls?: string[];
}

/**
 * Amber Path: POST scripts/suggest → poll `/persona-opportunity-sets/status/:id`.
 * Opens the generation task sidebar on the 202; invalidates scripts + set caches on success.
 */
export function useSuggestPersonaScripts() {
  const queryClient = useQueryClient();

  return useAmberMutation<SuggestPersonaScriptsVariables>({
    mutationFn: async ({ setId, tenantId, description, variations, referenceUrls }) => {
      const result = await suggestPersonaScripts(setId, {
        tenantId,
        description,
        variations,
        referenceUrls,
      });
      revealGenerationTask(queryClient);
      return result;
    },
    pollBasePath: "/persona-opportunity-sets",
    invalidateKeys: [personaOpportunityScriptKeys.all, personaOpportunitySetKeys.all],
    maxAttempts: PERSONA_SCRIPTS_POLL_MAX_ATTEMPTS,
    intervalMs: PERSONA_SCRIPTS_POLL_INTERVAL_MS,
    onSuccess: (result) => {
      const persisted = readScriptsPersisted(result);
      const unreadableRaw = result.unreadableUrls;
      const unreadable = Array.isArray(unreadableRaw)
        ? unreadableRaw.filter((url): url is string => typeof url === "string")
        : [];
      if (unreadable.length > 0) {
        toast.warning(
          i18n.t("personaOpportunities.scripts.suggest.unreadableLinks", {
            defaultValue:
              "Couldn't read {{count}} of your reference links (paywalled or unavailable): {{urls}}. Scripts were generated from your description.",
            count: unreadable.length,
            urls: unreadable.join(", "),
          }),
        );
      }
      if (result.cancelled === true) {
        toast.info(
          i18n.t("personaOpportunities.bulk.generationCancelled", {
            defaultValue: "Generation cancelled — {{count}} script(s) saved",
            count: persisted,
          }),
        );
        return;
      }
      if (statusHasScriptFailures(result) || (result.hasErrors === true && persisted > 0)) {
        toast.warning(
          i18n.t("personaOpportunities.bulk.generatePartial", {
            defaultValue:
              "Generated {{persisted}} of {{total}} scripts — check the Scripts tab for results",
            persisted,
            total: typeof result.total === "number" ? result.total : persisted,
          }),
        );
        return;
      }
      toast.success(
        i18n.t("personaOpportunities.bulk.generateSuccess", {
          defaultValue_one: "Generated {{count}} script",
          defaultValue_other: "Generated {{count}} scripts",
          count: persisted,
        }),
      );
    },
  });
}

export interface ImportPersonaScriptFromTextVariables {
  setId: string;
  tenantId: string;
  text: string;
}

export interface UseImportPersonaScriptFromTextOptions {
  /** Fires after import POST 202 — close the modal; task progress owns the poll. */
  onAccepted?: (variables: ImportPersonaScriptFromTextVariables) => void;
}

/**
 * Amber Path: POST scripts/import → poll until terminal. Close on 202; POST 4xx
 * stay in-modal (skipErrorToast). Worker FAILED after accept uses the error toast.
 */
export function useImportPersonaScriptFromText(
  options: UseImportPersonaScriptFromTextOptions = {},
) {
  const queryClient = useQueryClient();

  return useAmberMutation<ImportPersonaScriptFromTextVariables>({
    mutationFn: async ({ setId, tenantId, text }) => {
      const result = await importPersonaScriptFromText(setId, { tenantId, text });
      revealGenerationTask(queryClient);
      return result;
    },
    onAccepted: options.onAccepted,
    pollBasePath: "/persona-opportunity-sets",
    invalidateKeys: [personaOpportunityScriptKeys.all, personaOpportunitySetKeys.all],
    maxAttempts: PERSONA_SCRIPTS_POLL_MAX_ATTEMPTS,
    intervalMs: PERSONA_SCRIPTS_POLL_INTERVAL_MS,
    skipErrorToast: (error) => error instanceof ApiError,
    onSuccess: () => {
      toast.success(
        i18n.t("personaOpportunities.scripts.import.success", {
          defaultValue: "Imported script",
        }),
      );
    },
  });
}

export interface ExtractPersonaScriptImportVariables {
  setId: string;
  tenantId: string;
  file: File;
}

export interface ImportPersonaScriptsFromLegacyVariables {
  setId: string;
  tenantId: string;
  legacySetId: string;
  scriptIds: string[];
}

/** Sync POST scripts/import/legacy. Caller owns error copy (skipGlobalError). */
export function useImportPersonaScriptsFromLegacy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      setId,
      tenantId,
      legacySetId,
      scriptIds,
    }: ImportPersonaScriptsFromLegacyVariables) =>
      importPersonaScriptsFromLegacy(setId, { tenantId, legacySetId, scriptIds }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: personaOpportunityScriptKeys.all }),
        queryClient.invalidateQueries({ queryKey: personaOpportunitySetKeys.all }),
      ]);
      toast.success(
        i18n.t("personaOpportunities.scripts.import.legacySuccess", {
          count: result.importedIds.length,
          defaultValue_one: "Imported {{count}} script",
          defaultValue_other: "Imported {{count}} scripts",
        }),
      );
    },
    onError: (err) => {
      Sentry.captureException(err);
    },
    meta: { skipGlobalError: true },
  });
}

export function useExtractPersonaScriptImport() {
  return useMutation({
    mutationFn: ({ setId, tenantId, file }: ExtractPersonaScriptImportVariables) =>
      extractPersonaScriptImport(setId, file, tenantId),
    meta: { skipGlobalError: true },
  });
}

export interface ResolvePersonaReferenceLinksVariables {
  setId: string;
  referenceUrls: string[];
  tenantId: string;
}

export interface PersonaResolvedLinkResult {
  url: string;
  ok: boolean;
  reason: string;
}

/**
 * Amber Path: POST scripts/resolve-links → poll until terminal.
 * No success toast — the Suggest Script modal drives its own UX.
 */
export function useResolvePersonaReferenceLinks() {
  return useAmberMutation<ResolvePersonaReferenceLinksVariables>({
    mutationFn: ({ setId, referenceUrls, tenantId }) =>
      resolvePersonaReferenceLinks(setId, referenceUrls, tenantId),
    pollBasePath: "/persona-opportunity-sets",
    maxAttempts: RESOLVE_LINKS_POLL_MAX_ATTEMPTS,
    intervalMs: RESOLVE_LINKS_POLL_INTERVAL_MS,
  });
}

export function toPersonaResolvedLinkResults(result: unknown): PersonaResolvedLinkResult[] {
  const results = (result as { results?: unknown } | null)?.results;
  return Array.isArray(results) ? (results as PersonaResolvedLinkResult[]) : [];
}

export function personaScriptVersionHistoryQueryOptions(
  tenantId: string | null | undefined,
  setId: string,
  scriptId: string,
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityScriptKeys.versions(normalizedTenant, setId, scriptId),
    queryFn: () => fetchPersonaScriptVersionHistory(setId, scriptId),
    enabled: !!tenantId && !!setId && !!scriptId && (options.enabled ?? true),
  });
}

export function personaScriptVersionQueryOptions(
  tenantId: string | null | undefined,
  setId: string,
  scriptId: string,
  version: number | null,
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  const resolvedVersion = version ?? 0;
  return queryOptions({
    queryKey: personaOpportunityScriptKeys.version(
      normalizedTenant,
      setId,
      scriptId,
      resolvedVersion,
    ),
    queryFn: () => fetchPersonaScriptVersionHistory(setId, scriptId, resolvedVersion),
    placeholderData: keepPreviousData,
    enabled:
      !!tenantId &&
      !!setId &&
      !!scriptId &&
      version != null &&
      (options.enabled ?? true),
  });
}

export interface RestorePersonaScriptVersionVariables {
  setId: string;
  scriptId: string;
  version: number;
  tenantId: string;
}

export function useRestorePersonaScriptVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      setId,
      scriptId,
      version,
      tenantId,
    }: RestorePersonaScriptVersionVariables) =>
      restorePersonaScriptVersion(setId, scriptId, version, tenantId),
    onSuccess: async (_updated, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: personaOpportunityScriptKeys.all }),
        queryClient.invalidateQueries({
          queryKey: personaOpportunityScriptKeys.versions(
            normalizedPersonaOpportunitySetTenantId(variables.tenantId),
            variables.setId,
            variables.scriptId,
          ),
        }),
      ]);
      toast.success(
        i18n.t("personaOpportunities.scripts.history.restored", {
          defaultValue: "Restored version {{version}}",
          version: variables.version,
        }),
      );
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}
